import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { type TripPreferences } from '../../shared/types/trip';
import { useTravelPreferences } from '@/hooks/useUserProfile';
import { useAuth } from './AuthContext';

// Local type aliases derived from TripPreferences
type ServiceTier = TripPreferences['serviceTier'];

// Default trip preferences
const defaultTripPreferences: TripPreferences = {
  serviceTier: 'self_service',
  destination: {
    city: '',
    country: '',
    region: '',
    placeId: '',
    address: '',
    coordinates: {
      lat: 0,
      lng: 0,
    },
  },
  dates: {
    start: new Date(),
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
  },
  travelers: {
    adults: 2,
    children: 0,
    infants: 0,
    groupType: 'couple',
  },
  activityPreferences: {
    interests: [],
    priceLevel: 'moderate',
  },
  diningPreferences: {
    dietaryRestrictions: [],
  },
} as TripPreferences;

interface TripPreferencesContextType {
  preferences: TripPreferences;
  updateServiceTier: (tier: ServiceTier) => void;
  updateTripMotivation: (motivation: TripPreferences['tripMotivation']) => void;
  updateDestination: (
    destination: Partial<TripPreferences['destination']>
  ) => void;
  updateDates: (dates: Partial<TripPreferences['dates']>) => void;
  updateTravelers: (travelers: Partial<TripPreferences['travelers']>) => void;
  updateActivityPreferences: (
    activities: Partial<TripPreferences['activityPreferences']>
  ) => void;
  updateDiningPreferences: (
    dining: Partial<TripPreferences['diningPreferences']>
  ) => void;
  resetPreferences: () => void;
  // Initial preferences functionality
  setInitialPreferences: (preferences: Partial<TripPreferences>) => void;
  clearInitialPreferences: () => void;
  hasInitialPreferences: boolean;
}

const TripPreferencesContext = createContext<
  TripPreferencesContextType | undefined
>(undefined);

export const useTripPreferences = () => {
  const context = useContext(TripPreferencesContext);
  if (context === undefined) {
    throw new Error(
      'useTripPreferences must be used within a TripPreferencesProvider'
    );
  }
  return context;
};

// Memoized provider component
export const TripPreferencesProvider = React.memo(
  ({ children }: { children: ReactNode }) => {
    const [preferences, setPreferences] = useState<TripPreferences>(
      defaultTripPreferences
    );
    const [initialPreferences, setInitialPreferencesState] =
      useState<Partial<TripPreferences> | null>(null);
    const [hasAppliedUserPrefs, setHasAppliedUserPrefs] = useState(false);
    const [hasAppliedInitialPrefs, setHasAppliedInitialPrefs] = useState(false);

    const { currentUser } = useAuth();
    const { travelPreferences: userTravelPreferences } = useTravelPreferences();

    // Apply user preferences when they become available
    // NOTE: User prefs NEVER set destination - that only comes from landing page
    useEffect(() => {
      if (currentUser && userTravelPreferences && !hasAppliedUserPrefs) {
        setPreferences(prev => {
          const updated = {
            ...prev,
            serviceTier: 'self_service',
            // ALWAYS preserve destination - it only comes from landing page
            destination: prev.destination,
          } as TripPreferences;

          return updated;
        });
        setHasAppliedUserPrefs(true);
      }
    }, [currentUser, userTravelPreferences, hasAppliedUserPrefs]);

    // Apply initial preferences from landing page when they become available
    useEffect(() => {
      if (initialPreferences && !hasAppliedInitialPrefs) {
        setPreferences(prev => {
          // Create a new preferences object with the updated values
          // Reset interests to empty array if not provided in initialPreferences
          const updatedPreferences = {
            ...prev,
            serviceTier: initialPreferences.serviceTier || prev.serviceTier,
            destination: initialPreferences.destination || prev.destination,
            activityPreferences: initialPreferences.activityPreferences || {
              interests: [],
              priceLevel: prev.activityPreferences?.priceLevel || 'moderate',
            },
          };

          return updatedPreferences;
        });
        setHasAppliedInitialPrefs(true);
      }
    }, [initialPreferences, hasAppliedInitialPrefs]);

    // Initial preferences functions
    const setInitialPreferences = useCallback(
      (preferences: Partial<TripPreferences>) => {
        setInitialPreferencesState(preferences);
      },
      []
    );

    const clearInitialPreferences = useCallback(() => {
      setInitialPreferencesState(null);
      setHasAppliedInitialPrefs(false);
    }, []);

    const hasInitialPreferences = !!initialPreferences;

    // Memoized update functions to prevent unnecessary re-renders
    const updateServiceTier = useCallback((tier: ServiceTier): void => {
      setPreferences(prev => {
        const updatedPreferences = {
          ...prev,
          serviceTier: tier,
        };
        return updatedPreferences;
      });
      setHasAppliedUserPrefs(true);
      setHasAppliedInitialPrefs(true);
    }, []);

    const updateTripMotivation = useCallback(
      (motivation: TripPreferences['tripMotivation']): void => {
        setPreferences(prev => {
          const updatedPreferences = {
            ...prev,
            tripMotivation: motivation,
          };
          return updatedPreferences;
        });
      },
      []
    );

    const updateDestination = useCallback(
      (destination: Partial<TripPreferences['destination']>): void => {
        setPreferences(prev => {
          const updatedDestination = {
            ...prev.destination,
            ...destination,
          };

          const updatedPreferences = {
            ...prev,
            destination: updatedDestination as TripPreferences['destination'],
          };

          return updatedPreferences;
        });
      },
      []
    );

    const updateDates = useCallback(
      (dates: Partial<TripPreferences['dates']>): void => {
        setPreferences(prev => {
          const updatedPreferences = {
            ...prev,
            dates: { ...prev.dates, ...dates } as TripPreferences['dates'],
          };
          return updatedPreferences;
        });
      },
      []
    );

    const updateTravelers = useCallback(
      (travelers: Partial<TripPreferences['travelers']>): void => {
        setPreferences(prev => {
          const updatedPreferences = {
            ...prev,
            travelers: {
              ...prev.travelers,
              ...travelers,
            } as TripPreferences['travelers'],
          };
          return updatedPreferences;
        });
      },
      []
    );

    const updateActivityPreferences = useCallback(
      (activities: Partial<TripPreferences['activityPreferences']>): void => {
        setPreferences(prev => {
          const updatedPreferences = {
            ...prev,
            activityPreferences: {
              ...prev.activityPreferences,
              ...activities,
            } as TripPreferences['activityPreferences'],
          };
          return updatedPreferences;
        });
      },
      []
    );

    const updateDiningPreferences = useCallback(
      (dining: Partial<TripPreferences['diningPreferences']>): void => {
        setPreferences(prev => {
          const updatedPreferences = {
            ...prev,
            diningPreferences: {
              ...prev.diningPreferences,
              ...dining,
            } as TripPreferences['diningPreferences'],
          };
          return updatedPreferences;
        });
      },
      []
    );

    const resetPreferences = useCallback((): void => {
      setPreferences(defaultTripPreferences);
      setHasAppliedUserPrefs(false);
      setHasAppliedInitialPrefs(false);
    }, []);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(
      () => ({
        preferences,
        updateServiceTier,
        updateTripMotivation,
        updateDestination,
        updateDates,
        updateTravelers,
        updateActivityPreferences,
        updateDiningPreferences,
        resetPreferences,
        setInitialPreferences,
        clearInitialPreferences,
        hasInitialPreferences,
      }),
      [
        preferences,
        updateServiceTier,
        updateTripMotivation,
        updateDestination,
        updateDates,
        updateTravelers,
        updateActivityPreferences,
        updateDiningPreferences,
        resetPreferences,
        setInitialPreferences,
        clearInitialPreferences,
        hasInitialPreferences,
      ]
    );

    return (
      <TripPreferencesContext.Provider value={contextValue}>
        {children}
      </TripPreferencesContext.Provider>
    );
  }
);

TripPreferencesProvider.displayName = 'TripPreferencesProvider';
